import Timer16 from '@carbon/icons-react/es/timer/16';
import { Tooltip } from 'antd';
import React, { FC, ReactNode, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { TaskIdV2 } from 'api/models';
import { Project } from 'api/project';
import { Button } from 'components/atoms/button';
import { Maybe } from 'core/maybe';
import {
  ClickEventTags,
  LogType,
  setMarkerForDataDog,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { useGetProjectIdsOfCurrentUserFromIndexedDb } from 'hooks/use-idb';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { queueMacrotask } from 'utils/macrotask';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import { AddTimeEntryDrawer } from './add-time-entry-drawer';
import { Drawer } from './components';

interface Props {
  taskId: TaskIdV2;
  defaultBillable?: boolean;
  defaultCategory?: Maybe<number>;
  projectId: number;
  projectDetail?: Project;
  billableDisabled?: boolean;
}

const focusDurationOnShow = (visible: boolean) => {
  if (!visible) {
    return;
  }
  queueMacrotask(() => {
    const element = document.querySelector('#task_pane_time_entry_duration');

    if (element == null) {
      return;
    }
    (element as HTMLInputElement)?.focus();
  });
};

const isAllowedDueToStatus = ({
  projectStatus,
  allowedStatusToAddEntry,
}: {
  projectStatus?: number;
  allowedStatusToAddEntry: number[];
}) => {
  if (typeof projectStatus !== 'number') {
    return false;
  }
  const isEverythingAllowed = allowedStatusToAddEntry.length === 0;

  if (isEverythingAllowed) {
    return true;
  }

  return allowedStatusToAddEntry.includes(projectStatus);
};

const tooltips = {
  dueToStatus: (options) => ({
    id: 'task.add_time_entry.project_with_statuses.tooltip',
    values: {
      statuses: (
        <ul>
          {options?.map((status) => (
            <li>{status}</li>
          ))}
        </ul>
      ),
    },
  }),
  dueToUser: () => ({
    id: 'task.add_time_entry.not_part_of_project.tooltip',
  }),
};

export const AddTimeEntry: FC<Props> = ({
  taskId,
  defaultBillable,
  defaultCategory,
  projectId,
  projectDetail,
  billableDisabled,
}) => {
  const [visible, setVisible] = useState(false);
  const { data: configuration } = useTimesheetConfiguration();
  const myProjectIds = useGetProjectIdsOfCurrentUserFromIndexedDb();
  const isUserPartOfProject = useMemo(
    () => myProjectIds.includes(projectId),
    [myProjectIds, projectId],
  );
  const projectWithStatuses =
    configuration?.projectReportTimeSettings?.projectWithStatuses || [];
  const isAllowedDueToUser = configuration?.projectReportTimeSettings
    ?.yourProjectOnly
    ? isUserPartOfProject
    : true;

  const { data: projectCustomFields } = useProjectCustomFields();
  const statusFieldId = projectCustomFields.find(
    (field) => field.fieldName === 'Status',
  )?.fieldId;
  const status = projectDetail?.fields.find(
    (field) => field.fieldId === statusFieldId,
  );
  const isTimeEntryAllowedDueToStatus = isAllowedDueToStatus({
    projectStatus: status?.metaFieldValue?.value,
    allowedStatusToAddEntry: projectWithStatuses,
  });
  const isAllowed = useMemo(
    () => isTimeEntryAllowedDueToStatus && isAllowedDueToUser,
    [isAllowedDueToUser, isTimeEntryAllowedDueToStatus],
  );

  let tooltip = { id: 'task.add_time_entry.tooltip' } as {
    id: string;
    values?: ReactNode;
  };

  if (!isAllowed) {
    if (!isAllowedDueToUser) {
      tooltip = tooltips.dueToUser();
    } else if (!isTimeEntryAllowedDueToStatus) {
      tooltip = tooltips.dueToStatus(
        status?.metaData?.options
          ?.filter(
            (option) =>
              projectWithStatuses?.includes(option.value) && option?.active,
          )
          .map((option) => option.label),
      );
    }
  }

  const { executeLogger } = useLoadLoggerForDataDog(
    [ClickEventTags.TaskPaneTimeEntryDrawerOpen],
    LogType.CallbackLog,
  );

  return (
    <>
      <Tooltip
        title={
          <FormattedMessage
            id={tooltip.id}
            values={tooltip.values || undefined}
          />
        }
      >
        <Button
          data-cy="task.add_time_entry"
          type="text"
          size="small"
          icon={<Timer16 />}
          disabled={!isAllowed}
          onClick={() => {
            setMarkerForDataDog(ClickEventTags.TaskPaneTimeEntryDrawerOpen);
            setVisible(true);
          }}
        />
      </Tooltip>
      <Drawer
        visible={visible}
        onClose={() => setVisible(false)}
        afterVisibleChange={(isVisible) => {
          if (isVisible) {
            executeLogger();
          }
          focusDurationOnShow(isVisible);
        }}
      >
        <AddTimeEntryDrawer
          defaultBillable={defaultBillable}
          defaultCategory={defaultCategory}
          billableDisabled={billableDisabled}
          taskId={taskId}
          onClose={() => setVisible(false)}
          projectId={projectId}
        />
      </Drawer>
    </>
  );
};

export { ButtonGroup, Drawer } from './components';
export { DrawerContent } from './drawer-content';
